"use client";

import React, { useState, useRef, useEffect, useMemo } from "react";
import Container from "../container";
import { Form, Input, Button, App, message as staticMessage } from "antd";
import {
  GoogleMap,
  useJsApiLoader,
  Marker,
  InfoWindow,
} from "@react-google-maps/api";
import {
  IconMapPin,
  IconLocation,
  IconPlus,
  IconMinus,
  IconSend,
  IconCircleCheck,
  IconBrandWhatsappFilled,
  IconMailFilled,
  IconBrandMeta,
} from "@/lib/icons";
import { getRemixIcon } from "@/lib/remixIcon";
import CustomPhoneInput, {
  buildFullPhone,
} from "@/components/CustomPhoneInput";
import { nameValidation, emailValidation } from "@/lib/validations";
import styles from "./ContactUsSection.module.css";
import { getImageUrl } from "@/components/common/AppImage";

const OFFICE_COORDINATES = { lat: 23.11386, lng: 72.54131 };

// Turnstile can't load on the local dev server, so the captcha is skipped in
// development. The API route skips verification under the same condition.
const CAPTCHA_ENABLED = process.env.NODE_ENV !== "development";
const TURNSTILE_SITE_KEY = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY || "";
const TURNSTILE_SCRIPT_SRC =
  "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

let turnstileScriptPromise = null;

// Loads the Turnstile script once, only on pages that render the contact form.
function loadTurnstile() {
  if (typeof window === "undefined") return Promise.reject();
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (!turnstileScriptPromise) {
    turnstileScriptPromise = new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = TURNSTILE_SCRIPT_SRC;
      script.async = true;
      script.defer = true;
      script.onload = () => resolve(window.turnstile);
      script.onerror = () => {
        turnstileScriptPromise = null;
        reject(new Error("Failed to load Turnstile"));
      };
      document.head.appendChild(script);
    });
  }
  return turnstileScriptPromise;
}

const mapContainerStyle = {
  width: "100%",
  height: "100%",
};

const mapOptions = {
  minZoom: 5,
  maxZoom: 18,
  disableDefaultUI: true,
  zoomControl: false,
  mapTypeControl: false,
  streetViewControl: false,
  fullscreenControl: false,
  gestureHandling: "cooperative",
  scrollwheel: false,
  styles: [
    {
      featureType: "poi",
      elementType: "labels",
      stylers: [{ visibility: "off" }],
    },
    {
      featureType: "transit",
      elementType: "labels",
      stylers: [{ visibility: "off" }],
    },
  ],
};

const DEFAULT_SOCIALS = [
  {
    platform: "Instagram",
    url: "https://www.instagram.com/tykkitverse",
    icon: "instagram-line",
  },
  {
    platform: "Facebook",
    url: "https://www.facebook.com/tykkitverse",
    icon: "facebook-line",
  },
  {
    platform: "Twitter",
    url: "https://x.com/tykkitverse",
    icon: "twitter-x-fill",
  },
];

export default function ContactUsSection({ data }) {
  const [form] = Form.useForm();
  const antdApp = App?.useApp ? App.useApp() : null;
  const messageApi = antdApp?.message || staticMessage;

  const title = data?.title || "How can Help You?";
  const subtitle =
    data?.subtitle ||
    data?.description ||
    "Have questions? We'd love to hear from you.";
  const socialHandle = data?.social_handle ?? "@tykkitverse";
  const phone = data?.phone || "+91 73 200 79 200";
  const email = data?.email || "vibe@tykkit.com";
  const address =
    data?.address ||
    "A-405, GANESH GLORY, 11, off Sarkhej - Gandhinagar Highway, Jagatpur, Ahmedabad, Gujarat 382470, India";

  const socials =
    Array.isArray(data?.social) && data.social.length > 0
      ? data.social
      : DEFAULT_SOCIALS;

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submittedName, setSubmittedName] = useState("");
  // The success message is shorter than the form; keep the card's height so
  // the wheel and mic stay put instead of overlapping the contact cards.
  const cardRef = useRef(null);
  const [cardHeight, setCardHeight] = useState(null);

  // Turnstile captcha state
  const turnstileContainerRef = useRef(null);
  const turnstileWidgetIdRef = useRef(null);
  const [turnstileToken, setTurnstileToken] = useState("");
  const [captchaError, setCaptchaError] = useState("");

  // Map state
  const [activeMarker, setActiveMarker] = useState(false);
  const [logoImg, setLogoImg] = useState(null);
  const mapRef = useRef(null);
  const hoverTimeoutRef = useRef(null);

  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: process.env.NEXT_PUBLIC_GOOGLE_MAPS_KEY || "",
  });

  useEffect(() => {
    const img = new Image();
    img.onload = () => setLogoImg(img);
    img.src = "/logo.png";
  }, []);

  // Render the Turnstile widget whenever the form is visible (it unmounts on success).
  useEffect(() => {
    if (isSubmitted || !CAPTCHA_ENABLED || !TURNSTILE_SITE_KEY) return;
    let cancelled = false;

    loadTurnstile()
      .then((turnstile) => {
        const container = turnstileContainerRef.current;
        if (cancelled || !turnstile || !container) return;
        turnstileWidgetIdRef.current = turnstile.render(container, {
          sitekey: TURNSTILE_SITE_KEY,
          action: "contact",
          theme: "light",
          size: "normal",
          callback: (token) => {
            setTurnstileToken(token);
            setCaptchaError("");
          },
          "expired-callback": () => setTurnstileToken(""),
          "error-callback": () => {
            setTurnstileToken("");
            setCaptchaError(
              "Captcha failed to load. Please refresh and try again.",
            );
          },
        });
      })
      .catch(() => {
        if (!cancelled) {
          setCaptchaError(
            "Captcha failed to load. Please refresh and try again.",
          );
        }
      });

    return () => {
      cancelled = true;
      if (turnstileWidgetIdRef.current != null && window.turnstile) {
        window.turnstile.remove(turnstileWidgetIdRef.current);
      }
      turnstileWidgetIdRef.current = null;
      setTurnstileToken("");
    };
  }, [isSubmitted]);

  // Tokens are single-use, so get a fresh one after every submission attempt.
  const resetTurnstile = () => {
    setTurnstileToken("");
    if (turnstileWidgetIdRef.current != null && window.turnstile) {
      window.turnstile.reset(turnstileWidgetIdRef.current);
    }
  };

  const handleMouseOver = () => {
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    setActiveMarker(true);
  };

  const handleMouseOut = () => {
    hoverTimeoutRef.current = setTimeout(() => {
      setActiveMarker(false);
    }, 250);
  };

  const markerIcon = useMemo(() => {
    if (!isLoaded || !logoImg || typeof window === "undefined")
      return undefined;

    const displaySize = 56;
    const ring = 8;
    const dpr = Math.min(window.devicePixelRatio || 1, 3);
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(displaySize * dpr);
    canvas.height = Math.round(displaySize * dpr);
    const ctx = canvas.getContext("2d");
    ctx.scale(dpr, dpr);

    const cx = displaySize / 2;
    const cy = displaySize / 2;
    const outerR = displaySize / 2;
    const innerR = outerR - ring;

    ctx.save();
    ctx.shadowColor = "rgba(2, 21, 38, 0.3)";
    ctx.shadowBlur = 6;
    ctx.shadowOffsetY = 2;
    ctx.beginPath();
    ctx.arc(cx, cy, outerR - 0.5, 0, Math.PI * 2);
    ctx.fillStyle = "#ffffff";
    ctx.fill();
    ctx.restore();

    ctx.beginPath();
    ctx.arc(cx, cy, outerR, 0, Math.PI * 2);
    ctx.fillStyle = "#ffffff";
    ctx.fill();

    ctx.save();
    ctx.beginPath();
    ctx.arc(cx, cy, innerR, 0, Math.PI * 2);
    ctx.closePath();
    ctx.clip();
    ctx.drawImage(
      logoImg,
      ring,
      ring,
      displaySize - ring * 2,
      displaySize - ring * 2,
    );
    ctx.restore();

    ctx.beginPath();
    ctx.arc(cx, cy, outerR - 1.25, 0, Math.PI * 2);
    ctx.strokeStyle = "#ffffff";
    ctx.lineWidth = 2.5;
    ctx.stroke();

    return {
      url: canvas.toDataURL("image/png"),
      scaledSize: new window.google.maps.Size(displaySize, displaySize),
      anchor: new window.google.maps.Point(displaySize / 2, displaySize / 2),
      origin: new window.google.maps.Point(0, 0),
    };
  }, [isLoaded, logoImg]);

  const handleOpenDirections = () => {
    window.open(
      `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent("Ganesh Glory 11, Jagatpur, Ahmedabad, Gujarat 382470")}`,
      "_blank",
    );
  };

  const handleFormSubmit = async (values) => {
    setCardHeight(cardRef.current?.offsetHeight ?? null);
    if (CAPTCHA_ENABLED && !turnstileToken) {
      setCaptchaError("Please complete the captcha verification.");
      return;
    }

    try {
      setIsSubmitting(true);

      const payload = {
        name: values.name?.trim(),
        email: values.email?.trim() || null,
        // Dial code + digits, e.g. "919876543210".
        phone: buildFullPhone(
          values.country_code || "IN",
          String(values.phone || "").trim(),
        ),
        message: values.message?.trim(),
        turnstileToken,
      };

      // Goes through our API route, which verifies the captcha server-side
      // before forwarding the message to the backend.
      const res = await fetch("/api/contact-us", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const response = await res.json().catch(() => null);

      // Captcha rejected by our route (captchaFailed) or by the backend
      // (errors.turnstileToken). Either way the token is spent, so get a new one.
      const backendCaptchaError = response?.errors?.turnstileToken?.[0];
      if (response?.captchaFailed || backendCaptchaError) {
        const captchaMessage = backendCaptchaError || response.message;
        setCaptchaError(captchaMessage);
        messageApi.error(captchaMessage);
        resetTurnstile();
        return;
      }

      if (res.ok || response?.status || response?.success) {
        messageApi.success(
          response?.message ||
            "Thank you! Your message has been sent successfully.",
        );
        setSubmittedName(values.name);
        setIsSubmitted(true);
        form.resetFields();
      } else {
        if (response?.message) {
          messageApi.error(response.message);
          resetTurnstile();
        } else {
          messageApi.success(
            "Thank you! Your inquiry has been sent to our team.",
          );
          setSubmittedName(values.name);
          setIsSubmitted(true);
          form.resetFields();
        }
      }
    } catch (err) {
      console.error("[ContactUs] API error:", err);
      messageApi.success("Thank you! Your message has been received.");
      setSubmittedName(values.name);
      setIsSubmitted(true);
      form.resetFields();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    form.resetFields();
    setIsSubmitted(false);
  };

  // Decorative images come from the CMS; each one is skipped when not set.
  const decorImages = [
    { key: "top_left_image", className: styles.decorTopLeft },
    { key: "top_right_image", className: styles.decorTopRight },
    { key: "bottom_left_image", className: styles.decorBottomLeft },
    { key: "bottom_right_image", className: styles.decorBottomRight },
  ]
    .map((item) => ({
      ...item,
      src: getImageUrl(data?.[item.key], { useUrl: true }),
    }))
    .filter((item) => item.src);

  return (
    <section className={styles.section}>
      <Container>
        <div className={styles.header}>
          <h1 className={styles.title}>{title}</h1>
          <p className={styles.subtitle}>{subtitle}</p>
        </div>

        <div className={styles.formWrap}>
          {decorImages.map(({ key, src, className }) => (
            // eslint-disable-next-line @next/next/no-img-element
            <img
              key={key}
              src={src}
              alt=""
              aria-hidden="true"
              loading="lazy"
              className={`${styles.decor} ${className}`}
            />
          ))}

          <div
            ref={cardRef}
            className={styles.card}
            style={
              isSubmitted && cardHeight ? { minHeight: cardHeight } : undefined
            }
          >
            {isSubmitted ? (
              <div
                className={`py-10 px-2 text-center flex flex-col items-center justify-center ${styles.success}`}
              >
                <div className="w-16 h-16 rounded-full bg-[#E6F7F5] border border-[#0A9B8A]/30 flex items-center justify-center text-[#0A9B8A] mb-4">
                  <IconCircleCheck size={36} />
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-[#021526] mb-2">
                  Message Sent Successfully!
                </h3>
                <p className="text-sm text-[#64748B] max-w-md mx-auto mb-6 leading-relaxed">
                  Thank you,{" "}
                  <strong className="text-[#021526] font-semibold">
                    {submittedName || "there"}
                  </strong>
                  . We&apos;ve received your message and our team will get back
                  to you shortly.
                </p>
                <Button
                  type="primary"
                  onClick={handleReset}
                  className={styles.submit}
                  style={{ width: "auto" }}
                >
                  Send Another Message
                </Button>
              </div>
            ) : (
              <Form
                form={form}
                layout="vertical"
                onFinish={handleFormSubmit}
                className={styles.form}
              >
                <Form.Item
                  label="Full Name"
                  name="name"
                  rules={nameValidation("Full name", true, 60)}
                >
                  <Input placeholder="Full name" />
                </Form.Item>

                <div className={styles.row}>
                  <Form.Item
                    label="Mobile Number"
                    required
                    className={styles.phone}
                  >
                    <CustomPhoneInput placeholder="Number" popupWidth={160} />
                  </Form.Item>

                  <Form.Item
                    label={
                      <>
                        Email Address
                        <span className={styles.optional}>(Optional)</span>
                      </>
                    }
                    name="email"
                    rules={emailValidation(false)}
                  >
                    <Input type="email" placeholder="Email address" />
                  </Form.Item>
                </div>

                <Form.Item
                  label="Notes if Any"
                  name="message"
                  className={styles.notesItem}
                  rules={[
                    { required: true, message: "Please enter your message" },
                    {
                      min: 20,
                      message: "Message must be at least 20 characters",
                    },
                    {
                      max: 500,
                      message: "Message cannot exceed 500 characters",
                    },
                  ]}
                >
                  <Input.TextArea maxLength={500} placeholder="Start here..." />
                </Form.Item>

                <div className={styles.actions}>
                  {CAPTCHA_ENABLED && (
                    <Form.Item
                      className={styles.captchaItem}
                      validateStatus={captchaError ? "error" : undefined}
                      help={captchaError || undefined}
                    >
                      <div className={styles.captchaBox}>
                        <div
                          ref={turnstileContainerRef}
                          className={styles.captchaInner}
                        />
                      </div>
                    </Form.Item>
                  )}

                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={isSubmitting}
                    disabled={CAPTCHA_ENABLED && !turnstileToken}
                    icon={!isSubmitting && <IconSend size={20} stroke={1.75} />}
                    className={styles.submit}
                  >
                    {isSubmitting ? "Sending..." : "Send Message"}
                  </Button>
                </div>
              </Form>
            )}
          </div>
        </div>

        <div className={styles.contactCards}>
          <a
            href={`https://wa.me/${phone.replace(/[^\d]/g, "")}`}
            target="_blank"
            rel="noopener noreferrer"
            className={`${styles.contactCard} ${styles.whatsappCard}`}
          >
            <span className={`${styles.iconCircle} ${styles.whatsappIcon}`}>
              <IconBrandWhatsappFilled size={24} />
            </span>
            <div className={styles.cardText}>
              <p className={styles.cardLabel}>Whatsapp Support</p>
              <p className={styles.cardValue}>{phone}</p>
            </div>
          </a>

          <a
            href={`mailto:${email}`}
            className={`${styles.contactCard} ${styles.emailCard}`}
          >
            <span className={`${styles.iconCircle} ${styles.emailIcon}`}>
              <IconMailFilled size={24} />
            </span>
            <div className={styles.cardText}>
              <p className={styles.cardLabel}>Email Inquiry</p>
              <p className={styles.cardValue}>{email}</p>
            </div>
          </a>
        </div>

        {socials.length > 0 && (
          <div className={styles.socials}>
            {socials.map((social, idx) => {
              // The design uses the Meta mark for Facebook.
              const Icon = /facebook/i.test(
                social.icon || social.platform || "",
              )
                ? IconBrandMeta
                : getRemixIcon(social.icon);
              if (!Icon) return null;
              return (
                <a
                  key={idx}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={social.platform || "Social link"}
                  className={styles.socialLink}
                >
                  <Icon size={21} stroke={1.75} />
                </a>
              );
            })}
            {socialHandle && (
              <p className={styles.socialTagline}>
                Don&apos;t follow us
                <br />
                {socialHandle}
              </p>
            )}
          </div>
        )}
      </Container>

      {/* Full-width venue map */}
      <div className={styles.map}>
        {isLoaded ? (
          <>
            <GoogleMap
              mapContainerStyle={mapContainerStyle}
              center={OFFICE_COORDINATES}
              zoom={15}
              options={mapOptions}
              onLoad={(map) => {
                mapRef.current = map;
              }}
            >
              <Marker
                position={OFFICE_COORDINATES}
                icon={markerIcon}
                optimized={false}
                onMouseOver={handleMouseOver}
                onMouseOut={handleMouseOut}
                onClick={handleOpenDirections}
              >
                {activeMarker && (
                  <InfoWindow
                    onCloseClick={() => setActiveMarker(false)}
                    options={{
                      disableAutoPan: false,
                      pixelOffset:
                        isLoaded && window.google?.maps
                          ? new window.google.maps.Size(0, -10)
                          : undefined,
                    }}
                  >
                    <div
                      onMouseEnter={handleMouseOver}
                      onMouseLeave={handleMouseOut}
                      style={{
                        padding: "12px 14px 12px 14px",
                        maxWidth: "250px",
                        boxSizing: "border-box",
                        fontFamily: "inherit",
                      }}
                    >
                      <div
                        style={{
                          margin: "0 0 5px",
                          fontWeight: 700,
                          fontSize: "13px",
                          color: "#021526",
                          lineHeight: 1.3,
                        }}
                      >
                        Tykkit Headquarters
                      </div>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: "6px",
                          fontSize: "11px",
                          color: "#4a5568",
                          marginBottom: "10px",
                          lineHeight: 1.4,
                        }}
                      >
                        <IconMapPin
                          size={14}
                          style={{
                            color: "#021526",
                            flexShrink: 0,
                            marginTop: "2px",
                          }}
                        />
                        <span
                          style={{
                            display: "-webkit-box",
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                          }}
                        >
                          {address}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenDirections();
                        }}
                        style={{
                          width: "100%",
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: "6px",
                          padding: "7px 12px",
                          backgroundColor: "#021526",
                          color: "#ffffff",
                          border: "none",
                          borderRadius: "8px",
                          cursor: "pointer",
                          fontSize: "12px",
                          fontWeight: 600,
                          lineHeight: 1.4,
                          boxShadow: "0 2px 6px rgba(2, 21, 38, 0.15)",
                        }}
                      >
                        <IconLocation size={14} />
                        Get Directions
                      </button>
                    </div>
                  </InfoWindow>
                )}
              </Marker>
            </GoogleMap>

            {/* Map Zoom Controls */}
            <div className="absolute right-3 top-3 z-[1] flex flex-col overflow-hidden rounded-md border border-[#e3e6e9] bg-white shadow-sm">
              <button
                type="button"
                aria-label="Zoom in"
                onClick={() => {
                  const map = mapRef.current;
                  if (!map) return;
                  map.setZoom(Math.min((map.getZoom() || 15) + 1, 18));
                }}
                className="w-8 h-8 inline-flex items-center justify-center text-[#021526] hover:bg-[#f3f6f9] transition border-b border-[#eef1f4] cursor-pointer"
              >
                <IconPlus size={14} stroke={2.2} />
              </button>
              <button
                type="button"
                aria-label="Zoom out"
                onClick={() => {
                  const map = mapRef.current;
                  if (!map) return;
                  map.setZoom(Math.max((map.getZoom() || 15) - 1, 5));
                }}
                className="w-8 h-8 inline-flex items-center justify-center text-[#021526] hover:bg-[#f3f6f9] transition cursor-pointer"
              >
                <IconMinus size={14} stroke={2.2} />
              </button>
            </div>
          </>
        ) : (
          <div className="w-full h-full flex items-center justify-center text-xs text-[rgba(2,21,38,0.4)]">
            Loading map…
          </div>
        )}
      </div>
    </section>
  );
}
